import { Controller } from "@hotwired/stimulus"

// Collapse/expand a secondary sidebar, GitHub-style: a button at the bottom of
// the column folds it away, and its counterpart — pinned to the same corner —
// brings it back. Only one of the two is ever visible, so both share the single
// `toggle` action and the controller keeps their aria-expanded honest.
//
// It sits on the element WRAPPING both the sidebar and the content beside it
// (the collapsed class has to reach both), and everything the surface owns is a
// value or a class: the storage key, the collapsed class name. Layout is
// entirely CSS's business — this only flips the class and remembers it, so the
// choice survives navigating within the section. Two surfaces so far: the /docs
// TOC column (docs/_shell) and the /apps library sidebar (apps/index), which
// pairs it with apps-library on the same element.
//
// Every surface also gets a second, icon-only toggle flush right in the
// column's header row (shared/_sidebar_collapse_head_button): it shares the
// `toggle` target, so it stays in step with the footer button.
//
// `defaultCollapsed` starts a column folded until the member opens it (the
// calendar's drawer). `cookie` also writes the choice to a cookie named after
// the storage key, so the server can draw the column the way it was left -
// the calendar's drawer re-renders on every frame navigation, and a column
// that opened and then snapped shut each time would flicker.
//
// Deliberately not the docs-rail controller, which cycles a rail through
// shown -> expanded (-> hidden) and syncs an icon per state: this is one binary
// question with two buttons and no icon to rotate.
export default class extends Controller {
    static targets = ["toggle"]
    static classes = ["collapsed"]
    static values = {
        storageKey: { type: String, default: "sidebar_collapsed" },
        defaultCollapsed: { type: Boolean, default: false },
        cookie: { type: Boolean, default: false }
    }

    connect() {
        this._apply(this._stored())
    }

    // A toggle that arrives later (the /network toolbar re-renders inside its
    // list frame) starts out telling the truth about the column.
    toggleTargetConnected(button) {
        button.setAttribute("aria-expanded", this.element.classList.contains(this._collapsedClass()) ? "false" : "true")
    }

    toggle(event) {
        if (event) event.preventDefault()
        this._apply(!this.element.classList.contains(this._collapsedClass()))
    }

    _apply(collapsed) {
        this.element.classList.toggle(this._collapsedClass(), collapsed)
        this.toggleTargets.forEach((button) => {
            button.setAttribute("aria-expanded", collapsed ? "false" : "true")
        })
        this._persist(collapsed)
    }

    _collapsedClass() {
        return this.hasCollapsedClass ? this.collapsedClass : "is-side-collapsed"
    }

    // Nothing stored means "never chosen": shown, unless the surface asked to
    // start folded (defaultCollapsed) - a sidebar that hides itself on a first
    // visit with nothing to bring it back would just look broken.
    _stored() {
        try {
            const stored = window.localStorage.getItem(this.storageKeyValue)
            if (stored === null) return this.defaultCollapsedValue
            return stored === "collapsed"
        } catch {
            return this.defaultCollapsedValue
        }
    }

    _persist(collapsed) {
        try {
            window.localStorage.setItem(this.storageKeyValue, collapsed ? "collapsed" : "shown")
            if (this.cookieValue) {
                document.cookie = `${this.storageKeyValue}=${collapsed ? "collapsed" : "shown"}; path=/; max-age=31536000; samesite=lax`
            }
        } catch {
            // localStorage unavailable — the toggle still works for this view
        }
    }
}
